/**
 * What the person running "Run Test" is told when the test request itself fails
 * (as opposed to the node running and reporting a failure).
 *
 * Axios' own message ("Request failed with status code 404") means nothing to
 * a non-technical user and was being printed under the real reason (AB#22529).
 * The server's `detail`, when it is a plain sentence ("Contact not found"), is
 * already readable and is used as is; everything else gets a sentence that says
 * what to do next. The raw error is never returned.
 */
export function describeTestRequestError(error: unknown): string {
  const e = error as {
    code?: string
    response?: { status?: number; data?: { detail?: unknown } }
  } | null | undefined

  const detail = e?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail.trim()
  // FastAPI validation errors arrive as a list of objects, not a sentence.
  if (Array.isArray(detail)) return 'Some of this step’s settings are missing or not valid. Check them and try again.'

  if (!e?.response) {
    if (e?.code === 'ECONNABORTED') return 'The test took too long to finish. Please try again.'
    return 'We couldn’t reach the server. Check your internet connection and try again.'
  }

  switch (e.response.status) {
    case 401: return 'Your session has expired. Please sign in again.'
    case 403: return 'You don’t have permission to run this test.'
    case 404: return 'We couldn’t find what this test needs. Refresh the page and try again.'
    case 429: return 'Too many requests right now. Please wait a moment and try again.'
    default:
      if ((e.response.status ?? 0) >= 500) return 'Something went wrong on our side. Please try again in a moment.'
      return 'The test could not be run. Please try again.'
  }
}
